/**
 * Utilidades para manejar tokens JWT y extraer información de los claims
 */

export interface TokenClaims {
  [key: string]: any;
  exp?: number;
  iat?: number;
  iss?: string;
  aud?: string;
  sub?: string;
  name?: string;
  email?: string;
  roles?: string[];
  groups?: string[];
  // Claims específicos de Azure AD
  oid?: string;
  tid?: string;
  upn?: string;
  preferred_username?: string;
  // Claims personalizados que pueden contener roles
  extension_Roles?: string[];
  app_roles?: string[];
}

/**
 * Decodifica un token JWT y retorna los claims sin verificar la firma
 * @param token - Token JWT completo
 * @returns Claims del token o null si hay error
 */
export const decodeJWT = (token: string): TokenClaims | null => {
  try {
    // Un JWT tiene 3 partes separadas por puntos: header.payload.signature
    const parts = token.split('.');
    
    if (parts.length !== 3) {
      console.error('Token JWT inválido: debe tener 3 partes separadas por puntos');
      return null;
    }

    // Decodificar la parte del payload (claims)
    const payload = parts[1];
    
    // Agregar padding si es necesario para base64url
    const paddedPayload = payload + '='.repeat((4 - payload.length % 4) % 4);
    
    // Decodificar de base64url a string
    const decodedPayload = atob(paddedPayload.replace(/-/g, '+').replace(/_/g, '/'));
    
    // Parsear como JSON
    const claims = JSON.parse(decodedPayload);
    
    return claims;
  } catch (error) {
    console.error('Error al decodificar token JWT:', error);
    return null;
  }
};

/**
 * Extrae los roles del token JWT
 * @param token - Token JWT completo
 * @returns Array de roles o array vacío si no se encuentran
 */
export const extractRolesFromToken = (token: string): string[] => {
  const claims = decodeJWT(token);
  
  if (!claims) {
    return [];
  }

  // Buscar roles en diferentes claims posibles
  const possibleRoleClaims = [
    'roles',
    'groups', 
    'extension_Roles',
    'app_roles',
    'role'
  ];

  for (const claimName of possibleRoleClaims) {
    if (claims[claimName] && Array.isArray(claims[claimName])) {
      return claims[claimName];
    }
  }

  // Si no se encuentran roles en arrays, buscar en strings separados por comas
  for (const claimName of possibleRoleClaims) {
    if (claims[claimName] && typeof claims[claimName] === 'string') {
      return (claims[claimName] as string)
        .split(',')
        .map((role: string) => role.trim())
        .filter((role: string) => role.length > 0);
    }
  }

  console.warn('No se encontraron roles en el token JWT');
  return [];
};

/**
 * Extrae información básica del usuario del token
 * @param token - Token JWT completo
 * @returns Información del usuario o null si hay error
 */
export const extractUserInfoFromToken = (token: string): { name?: string; email?: string; username?: string; oid?: string } | null => {
  const claims = decodeJWT(token);
  
  if (!claims) {
    return null;
  }

  return {
    name: claims.name || claims.preferred_username,
    email: claims.email || claims.upn,
    username: claims.preferred_username || claims.upn || claims.name,
    oid: claims.oid
  };
};

/**
 * Verifica si el token está expirado
 * @param token - Token JWT completo
 * @returns true si está expirado, false si no
 */
export const isTokenExpired = (token: string): boolean => {
  const claims = decodeJWT(token);
  
  if (!claims || !claims.exp) {
    return true;
  }

  const currentTime = Math.floor(Date.now() / 1000);
  return claims.exp < currentTime;
};

/**
 * Obtiene el tiempo restante del token en segundos
 * @param token - Token JWT completo
 * @returns Tiempo restante en segundos o 0 si está expirado
 */
export const getTokenTimeRemaining = (token: string): number => {
  const claims = decodeJWT(token);
  
  if (!claims || !claims.exp) {
    return 0;
  }

  const currentTime = Math.floor(Date.now() / 1000);
  const timeRemaining = claims.exp - currentTime;
  
  return Math.max(0, timeRemaining);
};
